import { useEffect, useRef, useCallback } from 'react';

import { makeNamespacedLog } from '@sb/log';
import type { CommandResult } from '@sb/routine-runner';
import { TapIcon } from '@sb/ui/icons';
import { GUIDED_MODE_INTERVAL_MS, UserSession } from '@sbrc/utils';

import HeartbeatWorker from './guidedModeHeartbeatWorker?worker';
import { useRoutineRunnerHandle } from './useRoutineRunnerHandle';
import useToast from './useToast';

const log = makeNamespacedLog('useGuidedMode');

interface OnRunAdHocCommandArguments {
  onComplete?: () => void;
  onError?: (error?: string) => void;
  onGuidedModeStart?: () => void;
  onGuidedModeStop?: () => void;
  onRunCommand: () => Promise<void | CommandResult>;
  onSuccess?: () => void;
}

const MIN_HOLD_MS = 200;

type UseGuidedModeArguments = {
  isVizbot?: boolean;
};

/**
 * Build the WebSocket URL for the routine runner heartbeat worker.
 */
function getRoutineRunnerWsUrl(isVizbot: boolean): string {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const path = isVizbot ? 'vizbot-ws' : 'routine-runner';

  return `${wsProtocol}//${window.location.host}/${path}`;
}

/**
 * Ad-hoc commands can only be executed in the routine runner as long
 * as it is in Guided Mode. To let the routine runner know that Guided
 * Mode is still required, the remote control continually validates
 * the routine runner while the user holds down a Guided Mode action
 * on the page. Callbacks that handle this logic will create
 * intervals and store them here, and when the user releases the Guided
 * Mode action, the interval can be cancelled.
 */
const useGuidedMode = ({ isVizbot }: UseGuidedModeArguments) => {
  const { setToast } = useToast();

  const routineRunnerHandle = useRoutineRunnerHandle({ isVizbot });

  const workerRef = useRef<Worker | null>(null);

  const guidedModeStoppedAt = useRef<number>(Infinity);

  /**
   * Lazily create the Web Worker. The worker opens its own WebSocket to the
   * routine runner and sends `validateGuidedMode` on a timer. Because the
   * worker runs off the main thread, the heartbeat is immune to React renders
   * blocking `requestAnimationFrame` / `setInterval`.
   */
  const getWorker = useCallback(() => {
    if (!workerRef.current) {
      workerRef.current = new HeartbeatWorker();
    }

    return workerRef.current;
  }, []);

  const stopHeartbeatWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'stop' });
    }
  }, []);

  // Clean up worker on unmount
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'stop' });
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  /**
   * `stopGuidedMode` stops the heartbeat worker and sends a stop command.
   * Optional onGuidedModeStop callback for flexibility in adding logic
   */
  const stopGuidedMode = useCallback(
    async (onGuidedModeStop?: () => void) => {
      stopHeartbeatWorker();
      onGuidedModeStop?.();

      if (guidedModeStoppedAt.current === Infinity) {
        guidedModeStoppedAt.current = Date.now();
      }

      if (routineRunnerHandle.getState()?.kind !== 'RoutineRunning') {
        await routineRunnerHandle.stop('Stopped by user');
      }
    },
    [routineRunnerHandle, stopHeartbeatWorker],
  );

  /**
   * Start sending `validateGuidedMode()` to the routine runner via a Web Worker.
   *
   * The worker opens a dedicated WebSocket connection and sends heartbeat
   * messages on its own timer, completely off the main thread. This ensures the
   * 300 ms backend timeout is never hit, even when heavy React renders block
   * the main thread for extended periods (e.g. large routines with hundreds of
   * step components causing >300 ms render passes).
   *
   * We also fire one immediate validation on the main thread so the backend
   * sees the heartbeat before the worker's WebSocket connection is established.
   *
   * NB `startGuidedMode` is *sync*: we need to avoid any async code before the
   * heartbeat is started, otherwise `stopGuidedMode` could run first.
   */
  const startGuidedMode = useCallback(
    (onGuidedModeStart?: () => void) => {
      // Fire one immediate validation on the main thread's existing connection
      routineRunnerHandle.validateGuidedMode();

      // Start the off-thread heartbeat worker
      const worker = getWorker();
      const token = localStorage.getItem('feathers-jwt');
      const wsUrl = getRoutineRunnerWsUrl(Boolean(isVizbot));

      worker.postMessage({
        type: 'start',
        wsUrl,
        token,
        intervalMs: GUIDED_MODE_INTERVAL_MS,
      });

      onGuidedModeStart?.();
    },
    [routineRunnerHandle, getWorker, isVizbot],
  );

  const runAdHocCommand = useCallback(
    async ({
      onComplete,
      onError,
      onGuidedModeStart,
      onGuidedModeStop,
      onRunCommand,
      onSuccess,
    }: OnRunAdHocCommandArguments) => {
      startGuidedMode(onGuidedModeStart);
      const guidedModeStatedAt = Date.now();
      guidedModeStoppedAt.current = Infinity;

      const handleError = async (errorMessage: string) => {
        await routineRunnerHandle.stop(errorMessage);

        if (onError) {
          onError(errorMessage);
        } else {
          log.error(`runAdHocCommand.error`, 'Error running adhoc command', {
            message: errorMessage,
          });
        }
      };

      try {
        if (!isVizbot) {
          // Show the current user as running an ad-command command.
          UserSession.moveRobot(true);
        }

        const commandResult = await onRunCommand();

        /**
         * Some ad-hoc commands don't throw an error when they fail. Instead, they return
         * a string specifying the outcome of the command.
         */
        if (
          commandResult &&
          commandResult !== 'Success' &&
          commandResult !== 'Interrupted'
        ) {
          handleError(`Failure: ${commandResult}`);
        }

        if (commandResult === 'Success' && onSuccess) {
          onSuccess();
        }

        if (
          (commandResult === 'Interrupted' || commandResult === undefined) &&
          guidedModeStoppedAt.current - guidedModeStatedAt < MIN_HOLD_MS
        ) {
          setToast({
            kind: 'top',
            renderAsComponent: true,
            message: (
              <>
                <TapIcon size="small" className="tw-inline-block tw-mr-8" />
                Hold the button to apply changes
              </>
            ),
          });
        }
      } catch (e) {
        handleError(e instanceof Error ? e.message : String(e));
      } finally {
        stopGuidedMode(onGuidedModeStop);

        // wait up to 2 seconds for state to stop being RunningAdHocCommand
        await routineRunnerHandle.waitForState((state) => {
          return (
            state.kind !== 'RunningAdHocCommand' && state.kind !== 'Recovering'
          );
        }, 2000);

        if (onComplete) onComplete();

        if (!isVizbot) {
          // Makes sure this user is no longer marked as "running ad-hoc command".
          UserSession.moveRobot(false);
        }
      }
    },
    [isVizbot, routineRunnerHandle, startGuidedMode, stopGuidedMode, setToast],
  );

  return {
    startGuidedMode,
    stopGuidedMode,
    runAdHocCommand,
  };
};

export default useGuidedMode;
